/**
 * Printable "blanks to pull" sheet for the press station. Renders, for the currently-shown press runs,
 * the physical blanks an operator gathers off the shelf before pressing — a de-duped session total
 * (each shirt once) plus a per-run breakdown (what passes each screen / heat-press setup).
 *
 * Reuses the batch pick-list / purchase-order print mechanism: a body-level portal (`id="press-pull-
 * print"`) that `@media print` isolates (globals.css hides every other body child), plus
 * `window.print()`. No external print/PDF dependency. Pure display of worklist data already loaded —
 * no writes, no new endpoint.
 */
import { Printer, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import {
  type BlankName,
  type BlankPullRow,
  blankPullList,
  dedupeGarments,
  methodLabelOf,
  type PressRun,
} from './press-station-lib';

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

/** One "grab N of this blank" table — a tick box, the blank label, its colour·size, and the count. */
function PullTable({ rows }: { rows: BlankPullRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No blanks resolved for this run.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[420px] text-sm">
        <thead className="bg-muted/40">
          <tr>
            <th className="w-8 px-3 py-2 text-center text-xs font-medium text-muted-foreground">
              ✓
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Blank</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Colour · Size
            </th>
            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Qty</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t align-top">
              <td className="px-3 py-2 text-center">
                {/* Empty check box — tick each blank as you pull it. */}
                <span className="inline-block size-3.5 rounded-[2px] border border-current align-middle" />
              </td>
              <td className="px-3 py-2">
                <div className="font-medium">{r.label}</div>
                {r.titles.length > 1 && (
                  <div className="text-xs text-muted-foreground">
                    {r.titles.length} products on this blank
                  </div>
                )}
              </td>
              <td className="px-3 py-2">{r.variant || '—'}</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{r.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PressPullSheet({
  runs,
  nameBySku,
  filterLabel,
  onClose,
}: {
  runs: readonly PressRun[];
  nameBySku: ReadonlyMap<string, BlankName>;
  /** Which technique filter the station is on ("All techniques" / "Screen print" / "DTF"). */
  filterLabel: string;
  onClose: () => void;
}) {
  // Escape closes the overlay (matches the pick-list / PO print behaviour).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // De-duped shelf pull — each garment once, even if it's pressed in several runs (front + back).
  const totalRows = blankPullList(dedupeGarments(runs), nameBySku);
  const totalShirts = totalRows.reduce((s, r) => s + r.quantity, 0);

  return createPortal(
    <div
      id="press-pull-print"
      role="dialog"
      aria-modal="true"
      aria-label="Blanks pull sheet"
      className="fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6 print:static print:overflow-visible print:bg-transparent print:p-0"
    >
      <div className="mx-auto max-w-3xl space-y-5">
        {/* Toolbar — screen only */}
        <div className="flex items-center justify-end gap-2 print:hidden">
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.print()}
            disabled={runs.length === 0}
          >
            <Printer className="size-4" />
            Print
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="size-4" />
            Close
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Fast Screen Print
            </div>
            <h1 className="text-xl font-semibold">Blanks pull sheet</h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {filterLabel} · {runs.length} press run{runs.length === 1 ? '' : 's'}
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div className="hidden print:block">Printed: {shortDate(new Date().toISOString())}</div>
          </div>
        </div>

        {runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No press runs to pull for.</p>
        ) : (
          <>
            {/* De-duped total — the physical shelf pull */}
            <section className="space-y-2">
              <h2 className="text-base font-semibold">Total blanks to pull — {totalShirts}</h2>
              <p className="text-xs text-muted-foreground">
                Pull each of these from the shelf <span className="font-medium">once</span>. A blank
                printed in more than one run (front + back) is listed here a single time.
              </p>
              <PullTable rows={totalRows} />
            </section>

            {/* Per-run breakdown — what to stage at each setup */}
            <section className="space-y-4">
              <h2 className="text-base font-semibold">By press run</h2>
              <p className="text-xs text-muted-foreground">
                Every garment that passes each setup. These counts{' '}
                <span className="font-medium">overlap</span> the total above — a front+back shirt
                appears under both of its runs.
              </p>
              {runs.map((run) => (
                <div key={run.key} className="space-y-1.5">
                  <h3 className="text-sm font-medium">
                    {methodLabelOf(run.method)} · <span className="capitalize">{run.location}</span>{' '}
                    · {run.designLabel}
                    <span className="ml-2 font-normal text-muted-foreground">
                      {run.totalGarments} garment{run.totalGarments === 1 ? '' : 's'}
                    </span>
                  </h3>
                  <PullTable rows={blankPullList(run.items, nameBySku)} />
                </div>
              ))}
            </section>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
